import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Trash2, Save, Download, Play, Pause, Sparkles, CheckCircle,
  Wrench, CircuitBoard, Bot, Pickaxe, Mountain, BookMarked, Link2, Unlink,
  Info, AlertTriangle, X,
} from 'lucide-react';
import { EXTRA_KITS, EXTRA_TEMPLATES } from './engineering/templates';
import { SCENES, PART_ART, PART_SPECS, PROMPT_LIBRARY } from './engineering/realistic';
import { toast } from 'sonner';

// Real-photo backdrops for immersive scenes.  Pexels CDN, licensed for
// free commercial use.  Photos are heavily darkened + blurred at edges so
// the interactive canvas stays legible on top.
const PHOTO_BG = {
  kitchen:    'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&w=1600',
  bathroom:   'https://images.pexels.com/photos/1454804/pexels-photo-1454804.jpeg?auto=compress&w=1600',
  garden:     'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg?auto=compress&w=1600',
  workshop:   'https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg?auto=compress&w=1600',
  factory:    'https://images.pexels.com/photos/3862617/pexels-photo-3862617.jpeg?auto=compress&w=1600',
  mining:     'https://images.pexels.com/photos/2117937/pexels-photo-2117937.jpeg?auto=compress&w=1600',
  // Molten steel closeup — pure orange glow, unmistakable foundry
  metallurgy: 'https://images.pexels.com/photos/2760242/pexels-photo-2760242.jpeg?auto=compress&w=1600',
  // Welder shot with visible arc + sparks in the dark
  welding:    'https://images.pexels.com/photos/8541044/pexels-photo-8541044.jpeg?auto=compress&w=1600',
  electrical: 'https://images.pexels.com/photos/586063/pexels-photo-586063.jpeg?auto=compress&w=1600',
  robotics:   'https://images.pexels.com/photos/2251247/pexels-photo-2251247.jpeg?auto=compress&w=1600',
  civil:      'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&w=1600',
  carpentry:  'https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg?auto=compress&w=1600',
  fitters:    'https://images.pexels.com/photos/3862130/pexels-photo-3862130.jpeg?auto=compress&w=1600',
};

// Alternate photos that swap in when the student adds a specific piece of
// furniture — so "empty kitchen" → base kitchen; "add cabinet" → kitchen
// with cabinet in the same wall; "add sink" → kitchen with sink installed.
const CONTEXT_PHOTO = {
  kitchen: {
    base:    'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&w=1600',
    cabinet: 'https://images.pexels.com/photos/6197118/pexels-photo-6197118.jpeg?auto=compress&w=1600',
    sink:    'https://images.pexels.com/photos/5824900/pexels-photo-5824900.jpeg?auto=compress&w=1600',
    tap:     'https://images.pexels.com/photos/6577902/pexels-photo-6577902.jpeg?auto=compress&w=1600',
    stove:   'https://images.pexels.com/photos/6802046/pexels-photo-6802046.jpeg?auto=compress&w=1600',
  },
  bathroom: {
    base:    'https://images.pexels.com/photos/1454804/pexels-photo-1454804.jpeg?auto=compress&w=1600',
    toilet:  'https://images.pexels.com/photos/7031607/pexels-photo-7031607.jpeg?auto=compress&w=1600',
    bathtub: 'https://images.pexels.com/photos/7031414/pexels-photo-7031414.jpeg?auto=compress&w=1600',
    shower:  'https://images.pexels.com/photos/7031619/pexels-photo-7031619.jpeg?auto=compress&w=1600',
  },
};

const BG_LABEL = {
  kitchen:    "You're inside a real Indian kitchen. Add cabinets, sink, tap, stove …",
  bathroom:   "You're inside a real bathroom. Add toilet, bathtub, shower stall …",
  garden:     "You're in a home garden. Add garden bed, sprinkler, pipes …",
  workshop:   "You're inside a carpenter's workshop. Add workbench, saw, planks …",
  factory:    "You're on the factory floor. Add conveyors, motors, sensors …",
  mining:     "You're inside a live mine. Add drills, ore carts, headlamps …",
  metallurgy: "You're inside a live foundry. Add furnace, ladle, ingot, rollers …",
  welding:    "You're inside a welding bay. Add plate, torch, gas cylinders …",
  electrical: "You're at an electronics workbench. Add MCU, resistor, LED …",
  robotics:   "You're in a robotics lab. Add chassis, sensors, motors …",
  civil:      "You're on a construction site. Add foundation, columns, beams …",
  carpentry:  "You're in a carpentry shop. Add plank, saw, chisel, drawer …",
  fitters:    "You're at a pipe-fitters bench. Add flange, bolt, gasket …",
};

const API_ENG = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------------
// COMPONENT KITS — one per engineering domain.
// Each part carries a set of ports (default: L / R).  Ports are anchor points
// students click to draw wires/pipes between two components.
// -----------------------------------------------------------------------------
const PORTS_LR = [
  { id: 'L', side: 'left' },
  { id: 'R', side: 'right' },
];
const PORTS_TB = [
  { id: 'T', side: 'top' },
  { id: 'B', side: 'bottom' },
];
const PORTS_4 = [
  { id: 'L', side: 'left' },
  { id: 'R', side: 'right' },
  { id: 'T', side: 'top' },
  { id: 'B', side: 'bottom' },
];

const KITS = {
  plumbing: {
    label: 'Plumbing', icon: Wrench, accent: 'sky',
    flowColour: '#38bdf8', flowLabel: 'Water',
    parts: [
      { id: 'tank', label: 'Water Tank', emoji: '🪣', shape: 'square', w: 70, h: 70, colour: '#1d4ed8', ports: [{ id: 'R', side: 'right' }, { id: 'B', side: 'bottom' }] },
      { id: 'pipe_h', label: 'Straight Pipe', emoji: '━', shape: 'rect', w: 70, h: 18, colour: '#0284c7', ports: PORTS_LR },
      { id: 'pipe_v', label: 'Vertical Pipe', emoji: '┃', shape: 'rect', w: 18, h: 70, colour: '#0284c7', ports: PORTS_TB },
      { id: 'elbow', label: 'Elbow Joint', emoji: '↩', shape: 'circle', w: 34, h: 34, colour: '#0369a1', ports: PORTS_4 },
      { id: 'tee', label: 'T-Joint', emoji: '⊤', shape: 'circle', w: 36, h: 36, colour: '#0369a1', ports: PORTS_4 },
      { id: 'valve', label: 'Ball Valve', emoji: '◉', shape: 'circle', w: 38, h: 38, colour: '#dc2626', ports: PORTS_LR },
      { id: 'tap', label: 'Tap / Faucet', emoji: '🚰', shape: 'square', w: 42, h: 42, colour: '#4b5563', ports: [{ id: 'L', side: 'left' }] },
      { id: 'pump', label: 'Pump', emoji: '⚙', shape: 'circle', w: 46, h: 46, colour: '#059669', ports: PORTS_LR },
      { id: 'drain', label: 'Drain', emoji: '⚫', shape: 'circle', w: 40, h: 40, colour: '#1f2937', ports: [{ id: 'T', side: 'top' }] },
      { id: 'shower', label: 'Shower', emoji: '🚿', shape: 'square', w: 42, h: 42, colour: '#0ea5e9', ports: [{ id: 'T', side: 'top' }] },
    ],
    challenges: [
      'Tank → pipe → valve → tap. Then hit Run.',
      'T-split a tank into two taps.',
      'Add a pump so water reaches an elevated shower.',
    ],
  },
  electrical: {
    label: 'Electrical', icon: CircuitBoard, accent: 'amber',
    flowColour: '#facc15', flowLabel: 'Current',
    parts: [
      { id: 'battery', label: 'Battery', emoji: '🔋', shape: 'rect', w: 60, h: 40, colour: '#eab308', ports: [{ id: 'L', side: 'left', role: '-' }, { id: 'R', side: 'right', role: '+' }] },
      { id: 'led', label: 'LED', emoji: '💡', shape: 'circle', w: 40, h: 40, colour: '#f59e0b', ports: PORTS_LR },
      { id: 'resistor', label: 'Resistor', emoji: '〰', shape: 'rect', w: 70, h: 22, colour: '#a3671e', ports: PORTS_LR },
      { id: 'switch', label: 'Switch', emoji: '🎚', shape: 'rect', w: 50, h: 30, colour: '#57534e', ports: PORTS_LR },
      { id: 'motor', label: 'DC Motor', emoji: '⚙', shape: 'circle', w: 46, h: 46, colour: '#7c3aed', ports: PORTS_LR },
      { id: 'capacitor', label: 'Capacitor', emoji: '⚡', shape: 'rect', w: 44, h: 30, colour: '#3b82f6', ports: PORTS_LR },
      { id: 'wire', label: 'Wire Node', emoji: '•', shape: 'circle', w: 22, h: 22, colour: '#111827', ports: PORTS_4 },
      { id: 'buzzer', label: 'Buzzer', emoji: '🔔', shape: 'circle', w: 40, h: 40, colour: '#ea580c', ports: PORTS_LR },
      { id: 'ground', label: 'Ground', emoji: '⏚', shape: 'square', w: 34, h: 30, colour: '#525252', ports: [{ id: 'T', side: 'top' }] },
      { id: 'sensor', label: 'Photo-cell', emoji: '🔆', shape: 'circle', w: 38, h: 38, colour: '#facc15', ports: PORTS_LR },
    ],
    challenges: [
      'Battery(+) → switch → LED → back to battery(−).',
      'Add a resistor before the LED so it survives.',
      'Series switch + parallel two LEDs.',
    ],
  },
  robotics: {
    label: 'Robotics', icon: Bot, accent: 'purple',
    flowColour: '#a855f7', flowLabel: 'Signal',
    parts: [
      { id: 'chassis', label: 'Chassis', emoji: '▢', shape: 'rect', w: 100, h: 60, colour: '#7c3aed', ports: PORTS_4 },
      { id: 'wheel', label: 'Wheel', emoji: '⚫', shape: 'circle', w: 34, h: 34, colour: '#111827', ports: [{ id: 'T', side: 'top' }] },
      { id: 'motor2', label: 'Servo Motor', emoji: '⚙', shape: 'square', w: 42, h: 42, colour: '#4338ca', ports: PORTS_LR },
      { id: 'sensor2', label: 'Ultrasonic', emoji: '📡', shape: 'circle', w: 42, h: 42, colour: '#059669', ports: [{ id: 'B', side: 'bottom' }] },
      { id: 'arm', label: 'Robot Arm', emoji: '🦾', shape: 'rect', w: 26, h: 80, colour: '#a855f7', ports: PORTS_TB },
      { id: 'gripper', label: 'Gripper', emoji: '🤏', shape: 'square', w: 42, h: 34, colour: '#d946ef', ports: [{ id: 'T', side: 'top' }] },
      { id: 'camera', label: 'Camera', emoji: '📷', shape: 'square', w: 40, h: 36, colour: '#1f2937', ports: [{ id: 'B', side: 'bottom' }] },
      { id: 'mcu', label: 'MCU', emoji: '💾', shape: 'rect', w: 54, h: 42, colour: '#16a34a', ports: PORTS_4 },
      { id: 'battery2', label: 'Battery Pack', emoji: '🔋', shape: 'rect', w: 60, h: 34, colour: '#f59e0b', ports: PORTS_LR },
      { id: 'led2', label: 'Status LED', emoji: '🔴', shape: 'circle', w: 22, h: 22, colour: '#ef4444', ports: [{ id: 'L', side: 'left' }] },
    ],
    challenges: [
      'MCU → 2 motors → 2 wheels. Battery powers the MCU.',
      'Add ultrasonic sensor at the front, wire back to MCU.',
      'Camera → MCU → gripper via robot arm.',
    ],
  },
  mining: {
    label: 'Mining', icon: Pickaxe, accent: 'orange',
    flowColour: '#f97316', flowLabel: 'Ore Flow',
    parts: [
      { id: 'ore_gold', label: 'Gold Ore', emoji: '🟡', shape: 'circle', w: 38, h: 38, colour: '#f59e0b', ports: [{ id: 'R', side: 'right' }] },
      { id: 'ore_iron', label: 'Iron Ore', emoji: '⚪', shape: 'circle', w: 38, h: 38, colour: '#78716c', ports: [{ id: 'R', side: 'right' }] },
      { id: 'ore_coal', label: 'Coal', emoji: '⬛', shape: 'square', w: 38, h: 38, colour: '#1f2937', ports: [{ id: 'R', side: 'right' }] },
      { id: 'drill', label: 'Rotary Drill', emoji: '🛠', shape: 'rect', w: 62, h: 34, colour: '#dc2626', ports: PORTS_LR },
      { id: 'excavator', label: 'Excavator', emoji: '🚜', shape: 'square', w: 70, h: 50, colour: '#f97316', ports: PORTS_LR },
      { id: 'truck', label: 'Haul Truck', emoji: '🚛', shape: 'rect', w: 80, h: 40, colour: '#84cc16', ports: PORTS_LR },
      { id: 'conveyor', label: 'Conveyor', emoji: '━━', shape: 'rect', w: 110, h: 22, colour: '#57534e', ports: PORTS_LR },
      { id: 'crusher', label: 'Crusher', emoji: '⚒', shape: 'square', w: 56, h: 56, colour: '#a3671e', ports: PORTS_LR },
      { id: 'helmet', label: 'Helmet', emoji: '⛑', shape: 'circle', w: 34, h: 34, colour: '#eab308', ports: [] },
      { id: 'shaft', label: 'Mine Shaft', emoji: '⬛', shape: 'rect', w: 40, h: 100, colour: '#292524', ports: [{ id: 'T', side: 'top' }] },
    ],
    challenges: [
      'Ore → excavator → truck.',
      'Add crusher + 2 conveyors between truck & processing.',
      'Underground: shaft → drill → coal.',
    ],
  },
  metallurgy: {
    label: 'Metallurgy', icon: Mountain, accent: 'zinc',
    flowColour: '#fb923c', flowLabel: 'Molten Flow',
    parts: [
      { id: 'furnace', label: 'Blast Furnace', emoji: '🏭', shape: 'square', w: 70, h: 90, colour: '#dc2626', ports: [{ id: 'R', side: 'right' }, { id: 'B', side: 'bottom' }] },
      { id: 'ladle', label: 'Ladle', emoji: '🥄', shape: 'circle', w: 46, h: 46, colour: '#f97316', ports: PORTS_LR },
      { id: 'ingot', label: 'Ingot', emoji: '🟫', shape: 'rect', w: 60, h: 26, colour: '#78716c', ports: PORTS_LR },
      { id: 'roller', label: 'Rolling Mill', emoji: '🎢', shape: 'rect', w: 90, h: 36, colour: '#525252', ports: PORTS_LR },
      { id: 'quench', label: 'Quench Tank', emoji: '💧', shape: 'rect', w: 70, h: 40, colour: '#0369a1', ports: PORTS_LR },
      { id: 'alloy_brass', label: 'Brass', emoji: '🟨', shape: 'circle', w: 34, h: 34, colour: '#ca8a04', ports: [{ id: 'R', side: 'right' }] },
      { id: 'alloy_bronze', label: 'Bronze', emoji: '🟠', shape: 'circle', w: 34, h: 34, colour: '#b45309', ports: [{ id: 'R', side: 'right' }] },
      { id: 'anvil', label: 'Anvil', emoji: '🔨', shape: 'rect', w: 60, h: 30, colour: '#1f2937', ports: [{ id: 'L', side: 'left' }] },
      { id: 'crucible', label: 'Crucible', emoji: '🏺', shape: 'square', w: 46, h: 52, colour: '#a3671e', ports: PORTS_LR },
      { id: 'tongs', label: 'Tongs', emoji: '🗜', shape: 'rect', w: 60, h: 14, colour: '#374151', ports: [] },
    ],
    challenges: [
      'Furnace → ladle → ingot mould.',
      'Ingot → rolling mill → quench tank.',
      'Crucible + alloy → anvil.',
    ],
  },
};

// -----------------------------------------------------------------------------
// STARTER TEMPLATES — 6 per kit (30 total).
// Each part is placed by xPct / yPct.  `connections` is an array of
// [fromIdx, fromPort, toIdx, toPort] tuples referencing the parts array.
// -----------------------------------------------------------------------------
const TEMPLATES = {
  plumbing: [
    { name: 'Basic House Supply', description: 'Tank → pipe → valve → tap', parts: [
      { typeId: 'tank', xPct: 6, yPct: 34 },
      { typeId: 'pipe_h', xPct: 22, yPct: 46 },
      { typeId: 'valve', xPct: 34, yPct: 44 },
      { typeId: 'pipe_h', xPct: 44, yPct: 46 },
      { typeId: 'tap', xPct: 60, yPct: 44 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L']] },
    { name: 'Kitchen T-Split', description: 'Tank splits to two taps via T-joint', parts: [
      { typeId: 'tank', xPct: 6, yPct: 22 },
      { typeId: 'pipe_h', xPct: 22, yPct: 38 },
      { typeId: 'tee', xPct: 34, yPct: 38 },
      { typeId: 'pipe_v', xPct: 40, yPct: 20 },
      { typeId: 'pipe_v', xPct: 40, yPct: 56 },
      { typeId: 'tap', xPct: 44, yPct: 12 },
      { typeId: 'tap', xPct: 44, yPct: 74 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'T',3,'B'],[2,'B',4,'T'],[3,'T',5,'L'],[4,'B',6,'L']] },
    { name: 'Pumped Shower', description: 'Pump lifts water to a shower', parts: [
      { typeId: 'tank', xPct: 6, yPct: 60 },
      { typeId: 'pipe_h', xPct: 22, yPct: 68 },
      { typeId: 'pump', xPct: 34, yPct: 66 },
      { typeId: 'pipe_v', xPct: 44, yPct: 32 },
      { typeId: 'shower', xPct: 44, yPct: 12 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'B'],[3,'T',4,'T']] },
    { name: 'Drain Loop', description: 'Sink → pipe → drain', parts: [
      { typeId: 'tap', xPct: 12, yPct: 20 },
      { typeId: 'pipe_v', xPct: 20, yPct: 38 },
      { typeId: 'elbow', xPct: 22, yPct: 58 },
      { typeId: 'pipe_h', xPct: 34, yPct: 60 },
      { typeId: 'drain', xPct: 54, yPct: 58 },
    ], connections: [[0,'L',1,'T'],[1,'B',2,'T'],[2,'R',3,'L'],[3,'R',4,'T']] },
    { name: 'Dual Tank Supply', description: 'Two tanks feed one tap via valves', parts: [
      { typeId: 'tank', xPct: 6, yPct: 12 },
      { typeId: 'tank', xPct: 6, yPct: 60 },
      { typeId: 'valve', xPct: 26, yPct: 22 },
      { typeId: 'valve', xPct: 26, yPct: 70 },
      { typeId: 'tee', xPct: 42, yPct: 46 },
      { typeId: 'tap', xPct: 62, yPct: 44 },
    ], connections: [[0,'R',2,'L'],[1,'R',3,'L'],[2,'R',4,'L'],[3,'R',4,'B'],[4,'R',5,'L']] },
    { name: 'Garden Hose System', description: 'Tank → valve → pipe run → tap', parts: [
      { typeId: 'tank', xPct: 4, yPct: 40 },
      { typeId: 'valve', xPct: 18, yPct: 50 },
      { typeId: 'pipe_h', xPct: 28, yPct: 52 },
      { typeId: 'pipe_h', xPct: 42, yPct: 52 },
      { typeId: 'pipe_h', xPct: 56, yPct: 52 },
      { typeId: 'tap', xPct: 72, yPct: 50 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L'],[4,'R',5,'L']] },
  ],
  electrical: [
    { name: 'LED Torch', description: 'Battery → switch → LED', parts: [
      { typeId: 'battery', xPct: 8, yPct: 42 },
      { typeId: 'switch', xPct: 30, yPct: 42 },
      { typeId: 'led', xPct: 52, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',0,'L']] },
    { name: 'Series Bulb + Resistor', description: 'Classic protective resistor', parts: [
      { typeId: 'battery', xPct: 8, yPct: 34 },
      { typeId: 'switch', xPct: 28, yPct: 34 },
      { typeId: 'resistor', xPct: 46, yPct: 34 },
      { typeId: 'led', xPct: 66, yPct: 34 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',0,'L']] },
    { name: 'Motor Driver', description: 'Battery drives a DC motor', parts: [
      { typeId: 'battery', xPct: 8, yPct: 46 },
      { typeId: 'switch', xPct: 28, yPct: 46 },
      { typeId: 'motor', xPct: 50, yPct: 44 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',0,'L']] },
    { name: 'Buzzer Alarm', description: 'Photo-cell triggers buzzer', parts: [
      { typeId: 'sensor', xPct: 10, yPct: 32 },
      { typeId: 'battery', xPct: 30, yPct: 32 },
      { typeId: 'buzzer', xPct: 52, yPct: 32 },
      { typeId: 'ground', xPct: 32, yPct: 66 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',0,'L'],[1,'R',3,'T']] },
    { name: 'Parallel LEDs', description: 'Two LEDs share a battery', parts: [
      { typeId: 'battery', xPct: 8, yPct: 40 },
      { typeId: 'wire', xPct: 26, yPct: 42 },
      { typeId: 'led', xPct: 44, yPct: 22 },
      { typeId: 'led', xPct: 44, yPct: 60 },
      { typeId: 'wire', xPct: 64, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'T',2,'L'],[1,'B',3,'L'],[2,'R',4,'T'],[3,'R',4,'B'],[4,'R',0,'L']] },
    { name: 'Capacitor Filter', description: 'Battery → capacitor smoothing → LED', parts: [
      { typeId: 'battery', xPct: 8, yPct: 42 },
      { typeId: 'capacitor', xPct: 28, yPct: 42 },
      { typeId: 'resistor', xPct: 48, yPct: 42 },
      { typeId: 'led', xPct: 68, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',0,'L']] },
  ],
  robotics: [
    { name: '2-Wheel Rover', description: 'Chassis, wheels, MCU + battery', parts: [
      { typeId: 'chassis', xPct: 34, yPct: 40 },
      { typeId: 'mcu', xPct: 42, yPct: 24 },
      { typeId: 'battery2', xPct: 62, yPct: 24 },
      { typeId: 'motor2', xPct: 26, yPct: 48 },
      { typeId: 'motor2', xPct: 58, yPct: 48 },
      { typeId: 'wheel', xPct: 24, yPct: 66 },
      { typeId: 'wheel', xPct: 58, yPct: 66 },
    ], connections: [[2,'L',1,'R'],[1,'L',3,'R'],[1,'R',4,'L'],[3,'B',5,'T'],[4,'B',6,'T']] },
    { name: 'Obstacle Rover', description: 'Rover + ultrasonic sensor', parts: [
      { typeId: 'chassis', xPct: 34, yPct: 46 },
      { typeId: 'sensor2', xPct: 42, yPct: 20 },
      { typeId: 'mcu', xPct: 42, yPct: 34 },
      { typeId: 'battery2', xPct: 62, yPct: 34 },
      { typeId: 'wheel', xPct: 26, yPct: 66 },
      { typeId: 'wheel', xPct: 60, yPct: 66 },
    ], connections: [[1,'B',2,'T'],[3,'L',2,'R'],[2,'B',0,'T'],[0,'L',4,'T'],[0,'R',5,'T']] },
    { name: 'Arm Manipulator', description: 'MCU drives a gripper arm', parts: [
      { typeId: 'mcu', xPct: 12, yPct: 40 },
      { typeId: 'battery2', xPct: 12, yPct: 20 },
      { typeId: 'arm', xPct: 44, yPct: 30 },
      { typeId: 'gripper', xPct: 44, yPct: 70 },
    ], connections: [[1,'B',0,'T'],[0,'R',2,'T'],[2,'B',3,'T']] },
    { name: 'Camera Bot', description: 'Camera → MCU → wheels', parts: [
      { typeId: 'camera', xPct: 44, yPct: 12 },
      { typeId: 'mcu', xPct: 42, yPct: 34 },
      { typeId: 'chassis', xPct: 34, yPct: 54 },
      { typeId: 'wheel', xPct: 26, yPct: 74 },
      { typeId: 'wheel', xPct: 60, yPct: 74 },
    ], connections: [[0,'B',1,'T'],[1,'B',2,'T'],[2,'L',3,'T'],[2,'R',4,'T']] },
    { name: 'Signal Beacon', description: 'Sensor toggles a status LED', parts: [
      { typeId: 'sensor2', xPct: 20, yPct: 20 },
      { typeId: 'mcu', xPct: 40, yPct: 40 },
      { typeId: 'led2', xPct: 66, yPct: 40 },
      { typeId: 'battery2', xPct: 40, yPct: 66 },
    ], connections: [[0,'B',1,'T'],[1,'R',2,'L'],[3,'T',1,'B']] },
    { name: '4-Wheel Drive', description: 'Rover with 4 wheels + 4 motors', parts: [
      { typeId: 'chassis', xPct: 32, yPct: 44 },
      { typeId: 'mcu', xPct: 40, yPct: 24 },
      { typeId: 'battery2', xPct: 60, yPct: 24 },
      { typeId: 'wheel', xPct: 22, yPct: 62 },
      { typeId: 'wheel', xPct: 60, yPct: 62 },
      { typeId: 'wheel', xPct: 22, yPct: 78 },
      { typeId: 'wheel', xPct: 60, yPct: 78 },
    ], connections: [[2,'L',1,'R'],[1,'B',0,'T'],[0,'L',3,'T'],[0,'R',4,'T'],[0,'B',5,'T']] },
  ],
  mining: [
    { name: 'Open-Pit Setup', description: 'Ore → excavator → truck', parts: [
      { typeId: 'ore_iron', xPct: 6, yPct: 42 },
      { typeId: 'excavator', xPct: 22, yPct: 40 },
      { typeId: 'truck', xPct: 50, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L']] },
    { name: 'Crusher Line', description: 'Ore → conveyor → crusher → truck', parts: [
      { typeId: 'ore_coal', xPct: 4, yPct: 42 },
      { typeId: 'conveyor', xPct: 16, yPct: 44 },
      { typeId: 'crusher', xPct: 42, yPct: 40 },
      { typeId: 'conveyor', xPct: 56, yPct: 44 },
      { typeId: 'truck', xPct: 78, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L']] },
    { name: 'Gold Extraction', description: 'Ore → drill → crusher', parts: [
      { typeId: 'ore_gold', xPct: 6, yPct: 40 },
      { typeId: 'drill', xPct: 22, yPct: 40 },
      { typeId: 'excavator', xPct: 42, yPct: 40 },
      { typeId: 'crusher', xPct: 64, yPct: 40 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L']] },
    { name: 'Underground Mine', description: 'Shaft → drill → coal', parts: [
      { typeId: 'shaft', xPct: 44, yPct: 8 },
      { typeId: 'drill', xPct: 40, yPct: 62 },
      { typeId: 'ore_coal', xPct: 62, yPct: 64 },
    ], connections: [[0,'T',1,'L'],[1,'R',2,'R']] },
    { name: 'Twin Truck Yard', description: 'Two ores feed two trucks via crusher', parts: [
      { typeId: 'ore_iron', xPct: 4, yPct: 18 },
      { typeId: 'ore_coal', xPct: 4, yPct: 60 },
      { typeId: 'crusher', xPct: 34, yPct: 40 },
      { typeId: 'truck', xPct: 62, yPct: 18 },
      { typeId: 'truck', xPct: 62, yPct: 60 },
    ], connections: [[0,'R',2,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[2,'R',4,'L']] },
    { name: 'Long Conveyor', description: 'Ore hauled by three conveyors', parts: [
      { typeId: 'ore_gold', xPct: 2, yPct: 44 },
      { typeId: 'conveyor', xPct: 12, yPct: 46 },
      { typeId: 'conveyor', xPct: 34, yPct: 46 },
      { typeId: 'conveyor', xPct: 56, yPct: 46 },
      { typeId: 'truck', xPct: 78, yPct: 44 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L']] },
  ],
  metallurgy: [
    { name: 'Iron Reduction Line', description: 'Furnace → ladle → ingot', parts: [
      { typeId: 'furnace', xPct: 6, yPct: 16 },
      { typeId: 'ladle', xPct: 30, yPct: 44 },
      { typeId: 'ingot', xPct: 52, yPct: 46 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L']] },
    { name: 'Rolling Mill', description: 'Ingot → roller → quench', parts: [
      { typeId: 'ingot', xPct: 6, yPct: 44 },
      { typeId: 'roller', xPct: 26, yPct: 42 },
      { typeId: 'quench', xPct: 56, yPct: 42 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L']] },
    { name: 'Alloy Workbench', description: 'Crucible + alloys → anvil', parts: [
      { typeId: 'crucible', xPct: 8, yPct: 40 },
      { typeId: 'alloy_brass', xPct: 28, yPct: 22 },
      { typeId: 'alloy_bronze', xPct: 28, yPct: 60 },
      { typeId: 'anvil', xPct: 56, yPct: 44 },
    ], connections: [[0,'R',3,'L'],[1,'R',3,'L'],[2,'R',3,'L']] },
    { name: 'Complete Foundry', description: 'Furnace → ladle → ingot → roller → quench', parts: [
      { typeId: 'furnace', xPct: 4, yPct: 12 },
      { typeId: 'ladle', xPct: 22, yPct: 44 },
      { typeId: 'ingot', xPct: 40, yPct: 46 },
      { typeId: 'roller', xPct: 58, yPct: 44 },
      { typeId: 'quench', xPct: 80, yPct: 44 },
    ], connections: [[0,'R',1,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L']] },
    { name: 'Twin Crucible Cast', description: 'Two crucibles → shared ingot', parts: [
      { typeId: 'crucible', xPct: 6, yPct: 18 },
      { typeId: 'crucible', xPct: 6, yPct: 62 },
      { typeId: 'ingot', xPct: 44, yPct: 44 },
      { typeId: 'anvil', xPct: 66, yPct: 46 },
    ], connections: [[0,'R',2,'L'],[1,'R',2,'L'],[2,'R',3,'L']] },
    { name: 'Bronze Forge', description: 'Alloys → crucible → anvil', parts: [
      { typeId: 'alloy_brass', xPct: 6, yPct: 22 },
      { typeId: 'alloy_bronze', xPct: 6, yPct: 60 },
      { typeId: 'crucible', xPct: 30, yPct: 40 },
      { typeId: 'ingot', xPct: 52, yPct: 44 },
      { typeId: 'anvil', xPct: 72, yPct: 46 },
    ], connections: [[0,'R',2,'L'],[1,'R',2,'L'],[2,'R',3,'L'],[3,'R',4,'L']] },
  ],
};

const ACCENT_MAP = {
  sky:    { bg: 'bg-sky-500/15',    border: 'border-sky-500/40',    text: 'text-sky-400',    btn: 'bg-sky-600 hover:bg-sky-500' },
  amber:  { bg: 'bg-amber-500/15',  border: 'border-amber-500/40',  text: 'text-amber-400',  btn: 'bg-amber-600 hover:bg-amber-500' },
  purple: { bg: 'bg-purple-500/15', border: 'border-purple-500/40', text: 'text-purple-400', btn: 'bg-purple-600 hover:bg-purple-500' },
  orange: { bg: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-400', btn: 'bg-orange-600 hover:bg-orange-500' },
  zinc:   { bg: 'bg-zinc-500/15',   border: 'border-zinc-500/40',   text: 'text-zinc-300',   btn: 'bg-zinc-600 hover:bg-zinc-500' },
  stone:  { bg: 'bg-stone-500/15',  border: 'border-stone-500/40',  text: 'text-stone-300',  btn: 'bg-stone-600 hover:bg-stone-500' },
  red:    { bg: 'bg-red-500/15',    border: 'border-red-500/40',    text: 'text-red-400',    btn: 'bg-red-600 hover:bg-red-500' },
  slate:  { bg: 'bg-slate-500/15',  border: 'border-slate-500/40',  text: 'text-slate-300',  btn: 'bg-slate-600 hover:bg-slate-500' },
};

// Merge in Phase 40.11 extra kits + expanded templates. Extra library REPLACES
// the original 6 hand-crafted starters so every kit ends up with exactly 30
// templates distributed across the 5 difficulty tiers.
Object.assign(KITS, EXTRA_KITS);
Object.entries(EXTRA_TEMPLATES).forEach(([kitId, extras]) => {
  TEMPLATES[kitId] = extras;
});

// Difficulty tiers — grouped in the sidebar to keep 30 templates browsable.
const DIFFICULTY_TIERS = [
  { id: 'simple',        label: 'Simple',        colour: 'text-emerald-400' },
  { id: 'moderate',      label: 'Moderate',      colour: 'text-sky-400' },
  { id: 'intermediate',  label: 'Intermediate',  colour: 'text-indigo-400' },
  { id: 'advanced',      label: 'Advanced',      colour: 'text-amber-400' },
  { id: 'super_complex', label: 'Super Complex', colour: 'text-rose-400' },
];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function portPos(part, portId) {
  const port = (part.ports || []).find((p) => p.id === portId) || { side: 'right' };
  const cx = part.x + part.w / 2;
  const cy = part.y + part.h / 2;
  switch (port.side) {
    case 'left':   return { x: part.x, y: cy };
    case 'right':  return { x: part.x + part.w, y: cy };
    case 'top':    return { x: cx, y: part.y };
    case 'bottom': return { x: cx, y: part.y + part.h };
    default:       return { x: cx, y: cy };
  }
}

function curvedPath(a, b) {
  const dx = b.x - a.x;
  const cxOff = Math.max(40, Math.abs(dx) * 0.4);
  return `M ${a.x} ${a.y} C ${a.x + cxOff} ${a.y}, ${b.x - cxOff} ${b.y}, ${b.x} ${b.y}`;
}

// -----------------------------------------------------------------------------
// CanvasPart — draggable component with port dots
// -----------------------------------------------------------------------------
function CanvasPart({ p, onMove, onDelete, onPortClick, canvasRef, activePort, running, accent, zoom = 1, onHover }) {
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.dataset.port) return; // let port handler run
    e.stopPropagation();
    setDragging(true);
    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coords to logical (pre-scale) coords by dividing by zoom.
    offset.current = {
      x: (e.clientX - rect.left) / zoom - p.x,
      y: (e.clientY - rect.top) / zoom - p.y,
    };
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const logicalW = rect.width / zoom;
      const logicalH = rect.height / zoom;
      const nx = Math.max(0, Math.min(logicalW - p.w, (e.clientX - rect.left) / zoom - offset.current.x));
      const ny = Math.max(0, Math.min(logicalH - p.h, (e.clientY - rect.top) / zoom - offset.current.y));
      onMove(p.id, nx, ny);
    };
    const up = () => setDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging, p.id, p.w, p.h, onMove, canvasRef, zoom]);

  const radius = p.shape === 'circle' ? '50%' : p.shape === 'square' ? '6px' : '4px';
  const baseType = p.id.replace(/_\d+$/, '');
  const art = PART_ART[baseType];

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseEnter={() => onHover && onHover(p)}
      onMouseLeave={() => onHover && onHover(null)}
      className="absolute cursor-move select-none group"
      data-testid={`canvas-part-${p.id}`}
      style={{
        left: p.x, top: p.y, width: p.w, height: p.h,
        background: art ? 'transparent' : p.colour,
        borderRadius: art ? 0 : radius,
        boxShadow: dragging ? '0 10px 25px rgba(0,0,0,0.4)' : running ? `0 0 14px ${p.colour}aa, 0 0 4px ${p.colour}` : '0 4px 8px rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: dragging ? 'scale(1.05)' : 'scale(1)',
        transition: dragging ? 'none' : 'transform 0.15s ease, box-shadow 0.3s ease, filter 0.3s ease',
        border: art ? 'none' : '2px solid rgba(255,255,255,0.18)',
        zIndex: dragging ? 20 : 5,
        filter: running ? 'drop-shadow(0 0 3px rgba(255,255,255,0.15))' : 'none',
      }}
      title={p.label}
    >
      {art ? (
        <svg width={p.w} height={p.h} viewBox={`0 0 ${p.w} ${p.h}`} className="pointer-events-none overflow-visible">
          {art({ w: p.w, h: p.h, running, colour: p.colour })}
        </svg>
      ) : (
        <span className="text-lg leading-none pointer-events-none">{p.emoji}</span>
      )}

      {/* Ports */}
      {(p.ports || []).map((port) => {
        const pos = portPos(p, port.id);
        const localX = pos.x - p.x;
        const localY = pos.y - p.y;
        const isActive = activePort && activePort.partId === p.id && activePort.portId === port.id;
        return (
          <div
            key={port.id}
            data-port="1"
            data-testid={`port-${p.id}-${port.id}`}
            onMouseDown={(e) => { e.stopPropagation(); onPortClick(p.id, port.id); }}
            className="absolute rounded-full cursor-crosshair"
            style={{
              left: localX - 6, top: localY - 6, width: 12, height: 12,
              background: isActive ? '#facc15' : '#ffffff',
              border: `2px solid ${isActive ? '#f59e0b' : '#0f172a'}`,
              boxShadow: isActive ? '0 0 8px #facc15' : '0 1px 3px rgba(0,0,0,0.4)',
              zIndex: 10,
            }}
            title={`Port ${port.id}${port.role ? ' ('+port.role+')' : ''}`}
          />
        );
      })}

      <button
        onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity shadow"
        title="Delete"
        data-testid={`delete-part-${p.id}`}
      >×</button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MAIN
// -----------------------------------------------------------------------------
export default function EngineeringDesignerLab() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [params] = useSearchParams();
  const initialKit = params.get('kit') && KITS[params.get('kit')] ? params.get('kit') : 'plumbing';
  const [activeKit, setActiveKit] = useState(initialKit);
  const [placed, setPlaced] = useState([]);
  const [connections, setConnections] = useState([]); // [{id, fromPartId, fromPort, toPartId, toPort}]
  const [activePort, setActivePort] = useState(null); // {partId, portId}
  const [running, setRunning] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const [savedFlash, setSavedFlash] = useState(false);
  const nextId = useRef(1);
  const nextConnId = useRef(1);

  const kit = KITS[activeKit];
  const accent = ACCENT_MAP[kit.accent];

  const [personalTemplates, setPersonalTemplates] = useState([]);

  const _authHeaders = () => {
    const t = localStorage.getItem('token');
    const base = { 'Content-Type': 'application/json' };
    return t ? { ...base, Authorization: `Bearer ${t}` } : base;
  };

  // Cloud personal templates loader
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const localKey = `eng_designer_personal_${activeKit}`;
      let local = [];
      try { local = JSON.parse(localStorage.getItem(localKey) || '[]'); } catch { /* ignore */ }
      try {
        const r = await fetch(`${API_ENG}/api/engineering-templates/${activeKit}`, {
          credentials: 'include', headers: _authHeaders(),
        });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) {
            const cloud = data.templates || [];
            const seen = new Set(cloud.map((t) => t.name));
            const merged = [...cloud, ...local.filter((t) => !seen.has(t.name))];
            setPersonalTemplates(merged);
            localStorage.setItem(localKey, JSON.stringify(merged));
            return;
          }
        }
      } catch { /* fall through */ }
      if (!cancelled) setPersonalTemplates(local);
    })();
    return () => { cancelled = true; };
  }, [activeKit]);

  const persistPersonalTemplates = async (next) => {
    localStorage.setItem(`eng_designer_personal_${activeKit}`, JSON.stringify(next));
    try {
      await fetch(`${API_ENG}/api/engineering-templates/${activeKit}`, {
        method: 'PUT', credentials: 'include', headers: _authHeaders(),
        body: JSON.stringify({ kit: activeKit, templates: next }),
      });
    } catch { /* offline */ }
  };

  // Load persisted local design when kit switches
  useEffect(() => {
    setActivePort(null);
    setRunning(false);
    const saved = localStorage.getItem(`eng_designer_${activeKit}`);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setPlaced(data.placed || []);
        setConnections(data.connections || []);
        nextId.current = data.nextId || 1;
        nextConnId.current = data.nextConnId || 1;
      } catch { setPlaced([]); setConnections([]); }
    } else {
      setPlaced([]); setConnections([]);
      nextId.current = 1; nextConnId.current = 1;
    }
  }, [activeKit]);

  const addPart = (partDef, x, y) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const logicalW = rect.width / zoom;
    const logicalH = rect.height / zoom;
    const px = Math.max(0, Math.min(logicalW - partDef.w, x - partDef.w / 2));
    const py = Math.max(0, Math.min(logicalH - partDef.h, y - partDef.h / 2));
    const newId = `${partDef.id}_${nextId.current++}`;
    setPlaced((prev) => [...prev, { ...partDef, id: newId, x: px, y: py }]);
    setShowTip(false);
    return newId;
  };

  const movePart = (id, x, y) => {
    setPlaced((prev) => prev.map((p) => (p.id === id ? { ...p, x, y } : p)));
  };

  const deletePart = (id) => {
    setPlaced((prev) => prev.filter((p) => p.id !== id));
    setConnections((prev) => prev.filter((c) => c.fromPartId !== id && c.toPartId !== id));
  };

  const clearAll = () => {
    setPlaced([]); setConnections([]); setActivePort(null); setRunning(false);
    setPromptScene(null);
    toast.success('Canvas cleared');
  };

  // Phase 40.17 — Prompt-driven scene builder.  Parses simple English
  // ("empty kitchen", "add cabinet", "add sink", "clear") using
  // PROMPT_LIBRARY regexes and dispatches: scene change / drop furniture /
  // clear canvas.  No LLM required.
  const applyPrompt = (raw) => {
    const text = (raw || '').trim();
    if (!text) return;
    const canvas = canvasRef.current;
    const rect = canvas ? canvas.getBoundingClientRect() : { width: 900, height: 600 };
    let didSomething = false;

    // Commands
    for (const c of PROMPT_LIBRARY.commands) {
      if (c.match.test(text)) {
        if (c.action === 'clear')      { setPlaced([]); setConnections([]); setActivePort(null); setRunning(false); setPromptScene(null); toast.success('Canvas cleared'); return; }
        if (c.action === 'run')        { setRunning(true); toast.success('Simulation started'); return; }
      }
    }
    // Scene change
    for (const s of PROMPT_LIBRARY.scenes) {
      if (s.match.test(text)) {
        setPlaced([]); setConnections([]); setPromptScene(s.scene);
        toast.success(`Loaded: ${s.label}`);
        didSomething = true;
        break;
      }
    }
    // Furniture / parts — allow multiple in one prompt ("add cabinet and sink")
    // Kitchen SNAP-LAYOUT — cabinet against left wall, sink centred on
    // counter, tap on top of sink, stove right of sink, hood above stove.
    const SNAP = {
      cabinet: (r) => ({ x: 40, y: r.height * 0.45 }),
      sink_kitchen: (r) => ({ x: r.width * 0.38, y: r.height * 0.38 }),
      tap: (r) => ({ x: r.width * 0.42, y: r.height * 0.30 }),
      stove: (r) => ({ x: r.width * 0.60, y: r.height * 0.40 }),
      hood: (r) => ({ x: r.width * 0.60, y: r.height * 0.18 }),
      fridge: (r) => ({ x: r.width - 130, y: r.height * 0.30 }),
      toilet: (r) => ({ x: 60, y: r.height * 0.42 }),
      bathtub: (r) => ({ x: r.width * 0.35, y: r.height * 0.50 }),
      shower_stall: (r) => ({ x: r.width - 180, y: r.height * 0.25 }),
    };
    let placedCount = 0;
    const additions = [];
    for (const part of PROMPT_LIBRARY.parts) {
      if (part.match.test(text)) {
        const snap = SNAP[part.id];
        const pos = snap ? snap(rect) : {
          x: 60 + ((placed.length + placedCount) % 4) * 160 + Math.random() * 20,
          y: 80 + Math.floor((placed.length + placedCount) / 4) * 130 + Math.random() * 20,
        };
        additions.push({
          id: `${part.id}_${nextId.current++}`,
          label: part.label,
          emoji: part.emoji,
          w: part.w,
          h: part.h,
          colour: part.colour,
          shape: 'rect',
          x: Math.max(10, Math.min(pos.x, rect.width - part.w - 20)),
          y: Math.max(10, Math.min(pos.y, rect.height - part.h - 20)),
          ports: [{ id: 'a', side: 'left' }, { id: 'b', side: 'right' }],
        });
        placedCount++;
      }
    }
    if (additions.length) {
      setPlaced((prev) => [...prev, ...additions]);
      toast.success(`Added ${additions.length} ${additions.length === 1 ? 'item' : 'items'}: ${additions.map((a) => a.label).join(', ')}`);
      didSomething = true;
    }
    if (!didSomething) {
      toast.error(`Hmm — try "empty kitchen", "add cabinet", "add sink", "add tap"`);
    }
  };

  const saveDesign = () => {
    localStorage.setItem(`eng_designer_${activeKit}`, JSON.stringify({
      placed, connections, nextId: nextId.current, nextConnId: nextConnId.current,
    }));
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1400);
    toast.success(`${kit.label} design saved`);
  };

  const exportDesign = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const partsSvg = placed.map((p) => {
      const cx = p.x + p.w / 2;
      const cy = p.y + p.h / 2;
      if (p.shape === 'circle') {
        return `<circle cx="${cx}" cy="${cy}" r="${p.w / 2}" fill="${p.colour}" stroke="rgba(255,255,255,0.18)" stroke-width="2"/><text x="${cx}" y="${cy + 6}" text-anchor="middle" font-size="16">${p.emoji}</text>`;
      }
      return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="6" fill="${p.colour}" stroke="rgba(255,255,255,0.18)" stroke-width="2"/><text x="${cx}" y="${cy + 6}" text-anchor="middle" font-size="16">${p.emoji}</text>`;
    }).join('');
    const partsById = Object.fromEntries(placed.map((p) => [p.id, p]));
    const wiresSvg = connections.map((c) => {
      const a = partsById[c.fromPartId]; const b = partsById[c.toPartId];
      if (!a || !b) return '';
      const pa = portPos(a, c.fromPort); const pb = portPos(b, c.toPort);
      return `<path d="${curvedPath(pa, pb)}" stroke="${kit.flowColour}" stroke-width="3" fill="none" opacity="0.85"/>`;
    }).join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}" viewBox="0 0 ${rect.width} ${rect.height}"><rect width="100%" height="100%" fill="#0f172a"/>${wiresSvg}${partsSvg}<text x="12" y="24" fill="#94a3b8" font-family="sans-serif" font-size="14">${kit.label} Design — CognitiaIndia</text></svg>`;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kit.label.toLowerCase()}_design.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Design exported as SVG');
  };

  // Phase 40.15 — Blueprint export. Opens a new tab with a print-ready
  // A4 landscape diagram containing the current design + a labeled callout
  // list of every placed part with its real-world spec (from PART_SPECS).
  // Students press Ctrl+P (Cmd+P) to save as PDF.
  const exportBlueprint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (placed.length === 0) { toast.error('Add some parts before exporting a blueprint'); return; }
    const rect = canvas.getBoundingClientRect();
    const partsById = Object.fromEntries(placed.map((p) => [p.id, p]));
    // Build labelled SVG parts (rect/circle + emoji)
    const partsSvg = placed.map((p, i) => {
      const cx = p.x + p.w / 2;
      const cy = p.y + p.h / 2;
      const body = p.shape === 'circle'
        ? `<circle cx="${cx}" cy="${cy}" r="${p.w / 2}" fill="${p.colour}" stroke="#0f172a" stroke-width="1.5"/>`
        : `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="6" fill="${p.colour}" stroke="#0f172a" stroke-width="1.5"/>`;
      const number = `<circle cx="${p.x + p.w + 8}" cy="${p.y}" r="9" fill="#0369a1" stroke="#fff" stroke-width="1"/><text x="${p.x + p.w + 8}" y="${p.y + 4}" text-anchor="middle" fill="#fff" font-size="11" font-weight="900" font-family="sans-serif">${i + 1}</text>`;
      return body + `<text x="${cx}" y="${cy + 5}" text-anchor="middle" font-size="14">${p.emoji}</text>` + number;
    }).join('');
    const wiresSvg = connections.map((c) => {
      const a = partsById[c.fromPartId]; const b = partsById[c.toPartId];
      if (!a || !b) return '';
      const pa = portPos(a, c.fromPort); const pb = portPos(b, c.toPort);
      return `<path d="${curvedPath(pa, pb)}" stroke="${kit.flowColour}" stroke-width="2.5" fill="none" opacity="0.85"/>`;
    }).join('');
    // Callout table rows
    const rows = placed.map((p, i) => {
      const baseType = p.id.replace(/_\d+$/, '');
      const spec = PART_SPECS[baseType] || { title: p.label, spec: '—' };
      return `<tr><td class="n">${i + 1}</td><td class="t">${spec.title}</td><td class="s">${spec.spec}</td></tr>`;
    }).join('');
    const now = new Date().toLocaleString();
    const html = `<!doctype html>
<html><head>
<meta charset="utf-8" />
<title>${kit.label} Blueprint — CognitiaIndia</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; color: #0f172a; }
  .sheet { display: grid; grid-template-columns: 2fr 1fr; gap: 10mm; padding: 8mm; min-height: 190mm; }
  .diagram { border: 2px solid #0f172a; background: #f8fafc;
             background-image: linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px);
             background-size: 20px 20px; position: relative; }
  .diagram svg { width: 100%; height: 100%; display: block; }
  .side { display: flex; flex-direction: column; gap: 8px; }
  .title-block { border: 2px solid #0f172a; padding: 8px; }
  .title-block h1 { margin: 0; font-size: 18px; color: #0369a1; }
  .title-block .sub { font-size: 10px; color: #475569; margin-top: 2px; }
  .title-block .kv { display: grid; grid-template-columns: auto 1fr; column-gap: 8px; row-gap: 3px; font-size: 10px; margin-top: 8px; }
  .title-block .kv b { color: #334155; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th, td { border: 1px solid #64748b; padding: 4px 6px; text-align: left; }
  th { background: #0369a1; color: #fff; font-size: 10px; }
  td.n { width: 22px; text-align: center; font-weight: 700; color: #fff; background: #0369a1; }
  td.t { font-weight: 700; width: 34%; }
  td.s { font-family: 'Courier New', monospace; font-size: 9.5px; color: #065f46; }
  .footer { border-top: 1px solid #94a3b8; padding-top: 4px; font-size: 9px; color: #64748b; text-align: center; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } .noprint { display: none; } }
  .noprint { position: fixed; top: 6mm; right: 6mm; padding: 8px 14px; background: #0369a1; color: #fff; border: none; border-radius: 6px; font-weight: 700; cursor: pointer; }
</style>
</head>
<body>
  <button class="noprint" onclick="window.print()">🖨 Save as PDF</button>
  <div class="sheet">
    <div class="diagram">
      <svg viewBox="0 0 ${rect.width} ${rect.height}" preserveAspectRatio="xMidYMid meet">
        ${wiresSvg}
        ${partsSvg}
      </svg>
    </div>
    <div class="side">
      <div class="title-block">
        <h1>${kit.label.toUpperCase()} BLUEPRINT</h1>
        <div class="sub">CognitiaIndia · Young Engineer Lab</div>
        <div class="kv">
          <b>Drawing:</b><span>${kit.label} System Design</span>
          <b>Scale:</b><span>Not to scale · Concept diagram</span>
          <b>Parts:</b><span>${placed.length} components · ${connections.length} connections</span>
          <b>Prepared:</b><span>${now}</span>
        </div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Component</th><th>Specification</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">© CognitiaIndia · Educational blueprint · Values are indicative, not for construction.</div>
    </div>
  </div>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked — allow popups to export blueprint'); return; }
    w.document.write(html);
    w.document.close();
    toast.success('Blueprint opened — press Ctrl+P to save as PDF');
  };

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    if (activePort) { setActivePort(null); return; } // cancel wire draft
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    if (window.__pendingEngPart) {
      addPart(window.__pendingEngPart, x, y);
      window.__pendingEngPart = null;
      document.body.style.cursor = 'default';
    }
  };

  const handlePaletteClick = (partDef) => {
    window.__pendingEngPart = partDef;
    document.body.style.cursor = 'crosshair';
    toast.info(`Click on the canvas to place ${partDef.label}`);
  };

  const onPortClick = (partId, portId) => {
    if (!activePort) {
      setActivePort({ partId, portId });
      toast.info('Now click another port to connect');
      return;
    }
    if (activePort.partId === partId && activePort.portId === portId) {
      setActivePort(null);
      return;
    }
    if (activePort.partId === partId) {
      toast.error('Pick a port on a different component');
      return;
    }
    // avoid duplicates (either direction)
    const dup = connections.some((c) =>
      (c.fromPartId === activePort.partId && c.fromPort === activePort.portId && c.toPartId === partId && c.toPort === portId) ||
      (c.toPartId === activePort.partId && c.toPort === activePort.portId && c.fromPartId === partId && c.fromPort === portId)
    );
    if (dup) { toast.error('Already connected'); setActivePort(null); return; }
    setConnections((prev) => [...prev, {
      id: `w_${nextConnId.current++}`,
      fromPartId: activePort.partId, fromPort: activePort.portId,
      toPartId: partId, toPort: portId,
    }]);
    setActivePort(null);
    toast.success('Connected');
  };

  const deleteConnection = (id) => setConnections((prev) => prev.filter((c) => c.id !== id));

  const loadTemplate = (tpl) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const partsById = Object.fromEntries(kit.parts.map((p) => [p.id, p]));
    const idMap = [];
    const newParts = tpl.parts
      .map((t) => {
        const def = partsById[t.typeId];
        if (!def) { idMap.push(null); return null; }
        const newId = `${def.id}_${nextId.current++}`;
        idMap.push(newId);
        return {
          ...def,
          id: newId,
          x: Math.max(0, Math.min(rect.width - def.w, (t.xPct / 100) * rect.width)),
          y: Math.max(0, Math.min(rect.height - def.h, (t.yPct / 100) * rect.height)),
        };
      })
      .filter(Boolean);
    const newConns = (tpl.connections || []).map(([fi, fp, ti, tp]) => {
      const fromId = idMap[fi]; const toId = idMap[ti];
      if (!fromId || !toId) return null;
      return { id: `w_${nextConnId.current++}`, fromPartId: fromId, fromPort: fp, toPartId: toId, toPort: tp };
    }).filter(Boolean);
    setPlaced(newParts);
    setConnections(newConns);
    setShowTip(false);
    setRunning(false);
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const saveAsPersonalTemplate = async () => {
    if (placed.length === 0) { toast.error('Add some parts before saving as a template'); return; }
    const name = window.prompt('Name this template:', `My ${kit.label} design`);
    if (!name || !name.trim()) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const idxOf = Object.fromEntries(placed.map((p, i) => [p.id, i]));
    const tpl = {
      name: name.trim().slice(0, 50),
      description: `${placed.length} parts · ${connections.length} wires · ${new Date().toLocaleDateString()}`,
      parts: placed.map((p) => ({
        typeId: p.id.replace(/_\d+$/, ''),
        xPct: (p.x / rect.width) * 100,
        yPct: (p.y / rect.height) * 100,
      })),
      connections: connections.map((c) => [idxOf[c.fromPartId], c.fromPort, idxOf[c.toPartId], c.toPort]),
    };
    const next = [...personalTemplates, tpl];
    setPersonalTemplates(next);
    await persistPersonalTemplates(next);
    toast.success(`Saved as "${tpl.name}"`);
  };

  const deletePersonalTemplate = async (idx) => {
    const next = personalTemplates.filter((_, i) => i !== idx);
    setPersonalTemplates(next);
    await persistPersonalTemplates(next);
    toast.success('Template removed');
  };

  const [simReport, setSimReport] = useState(null); // { ok:[], issues:[], warnings:[] }
  const [activeTier, setActiveTier] = useState('all'); // Phase 40.11 — template difficulty filter
  const [zoom, setZoom] = useState(1); // Phase 40.12 — canvas zoom level (0.5 to 2)
  const [hoveredPart, setHoveredPart] = useState(null); // Phase 40.15 — spec callout on hover
  const [promptScene, setPromptScene] = useState(null); // Phase 40.17 — prompt-driven room backdrop
  const [promptInput, setPromptInput] = useState(''); // Phase 40.17 — prompt bar input

  // Structured message helper — Phase 40.12
  // Each item is { msg, fix } where fix (optional) describes an auto-repair:
  //   { kind:'add_only', typeId }
  //   { kind:'add_and_connect', typeId, toType, port?, fromPort? }  attach new part to nearest existing part of `toType`
  //   { kind:'insert_between', typeId, betweenTypes:[a,b] }  splices a new part into the wire connecting a→b
  const M = (msg, fix = null) => ({ msg, fix });

  // Heuristic critique engine — inspects placed parts + connections and produces
  // human-friendly feedback about the design.  Runs when the student hits Run.
  const analyzeDesign = () => {
    const partById = Object.fromEntries(placed.map((p) => [p.id, p]));
    const typeCount = placed.reduce((acc, p) => {
      const t = p.id.replace(/_\d+$/, '');
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const degree = {};
    placed.forEach((p) => { degree[p.id] = 0; });
    connections.forEach((c) => {
      degree[c.fromPartId] = (degree[c.fromPartId] || 0) + 1;
      degree[c.toPartId] = (degree[c.toPartId] || 0) + 1;
    });
    const orphans = placed.filter((p) => degree[p.id] === 0);

    const ok = []; const issues = []; const warnings = [];

    // Universal checks
    if (orphans.length > 0) {
      warnings.push(M(`${orphans.length} orphan part${orphans.length > 1 ? 's are' : ' is'} not wired to anything.`));
    } else if (placed.length > 1) {
      ok.push('Every part is connected to the network.');
    }

    // Kit-specific rules
    if (activeKit === 'plumbing') {
      const sources = ['tank'];
      const sinks = ['tap', 'shower', 'drain'];
      const hasSource = sources.some((s) => typeCount[s]);
      const hasSink = sinks.some((s) => typeCount[s]);
      if (!hasSource) issues.push(M('No water source — add a Water Tank so there is something to flow.', { kind: 'add_and_connect', typeId: 'tank', toType: 'pipe_h' }));
      else ok.push('Water source detected (tank).');
      if (!hasSink) issues.push(M('No outlet — add a Tap, Shower or Drain so water has somewhere to go.', { kind: 'add_and_connect', typeId: 'tap', toType: 'tank' }));
      else ok.push(`Outlets ready (${sinks.filter((s) => typeCount[s]).join(', ')}).`);
      if (!typeCount.valve && (typeCount.tap || typeCount.shower)) {
        warnings.push(M('No valve in the line — real plumbing needs a shut-off so users can stop the flow.',
          { kind: 'insert_between', typeId: 'valve', betweenTypes: ['tank', typeCount.tap ? 'tap' : 'shower'] }));
      }
      if (typeCount.shower && !typeCount.pump) {
        warnings.push(M('Elevated shower without a pump — gravity alone may not push water high enough.',
          { kind: 'insert_between', typeId: 'pump', betweenTypes: ['tank', 'shower'] }));
      }
    }

    if (activeKit === 'electrical') {
      const hasBattery = typeCount.battery;
      const loads = ['led', 'buzzer', 'motor', 'led2'];
      const hasLoad = loads.some((l) => typeCount[l]);
      if (!hasBattery) issues.push(M('No power source — add a Battery to drive the circuit.', { kind: 'add_and_connect', typeId: 'battery', toType: null }));
      else ok.push('Power source present (battery).');
      if (!hasLoad) issues.push(M('No load — add an LED, motor or buzzer so the current has work to do.', { kind: 'add_and_connect', typeId: 'led', toType: 'battery' }));
      else ok.push(`Loads present (${loads.filter((l) => typeCount[l]).join(', ')}).`);
      if (typeCount.led && !typeCount.resistor) {
        warnings.push(M('LED without a resistor — real LEDs burn out at battery voltage. Add a resistor in series.',
          { kind: 'insert_between', typeId: 'resistor', betweenTypes: ['battery', 'led'] }));
      }
      if (typeCount.battery && connections.length >= 1) {
        const batteryIds = placed.filter((p) => p.id.startsWith('battery_')).map((p) => p.id);
        const directLoop = connections.some((c) =>
          batteryIds.includes(c.fromPartId) && batteryIds.includes(c.toPartId)
        );
        if (directLoop) issues.push(M('Short-circuit risk — a wire runs battery-to-battery with no load between. Add a resistor or LED first.',
          { kind: 'insert_between', typeId: 'resistor', betweenTypes: ['battery', 'battery'] }));
      }
      if (hasBattery && hasLoad && connections.length < 2) {
        warnings.push(M('Circuit may be open — a full loop needs at least 2 wires between battery and load.'));
      }
    }

    if (activeKit === 'robotics') {
      if (!typeCount.mcu) issues.push(M('No microcontroller — add an MCU to make decisions.', { kind: 'add_and_connect', typeId: 'mcu', toType: 'battery2' }));
      else ok.push('Brain detected (MCU).');
      if (!typeCount.battery2) issues.push(M('No power pack — the MCU needs a battery.', { kind: 'add_and_connect', typeId: 'battery2', toType: 'mcu' }));
      else ok.push('Power pack wired.');
      if (typeCount.chassis && !typeCount.wheel) {
        warnings.push(M('Chassis without wheels — your rover cannot move yet.', { kind: 'add_and_connect', typeId: 'wheel', toType: 'chassis' }));
      }
      if (typeCount.wheel && !typeCount.motor2) {
        warnings.push(M('Wheels without motors — you need a servo/DC motor to spin them.', { kind: 'insert_between', typeId: 'motor2', betweenTypes: ['mcu', 'wheel'] }));
      }
    }

    if (activeKit === 'mining') {
      const ores = ['ore_gold', 'ore_iron', 'ore_coal'];
      const hasOre = ores.some((o) => typeCount[o]);
      if (!hasOre) issues.push(M('No ore in the layout — add gold, iron or coal to mine.', { kind: 'add_and_connect', typeId: 'ore_iron', toType: 'excavator' }));
      else ok.push('Ore body ready to extract.');
      if (!typeCount.truck && !typeCount.conveyor) {
        warnings.push(M('No transport — a truck or conveyor is needed to move ore.', { kind: 'add_and_connect', typeId: 'truck', toType: 'crusher' }));
      }
      if (typeCount.crusher) ok.push('Crusher stage present — ore will be processed.');
    }

    if (activeKit === 'metallurgy') {
      if (!typeCount.furnace && !typeCount.crucible) {
        issues.push(M('No heat source — add a Blast Furnace or Crucible to melt metal.', { kind: 'add_and_connect', typeId: 'furnace', toType: 'ladle' }));
      } else {
        ok.push('Heat source present.');
      }
      if (!typeCount.ingot && !typeCount.anvil && !typeCount.roller) {
        warnings.push(M('No output stage — add an ingot, roller or anvil so the molten metal has a shape to take.', { kind: 'add_and_connect', typeId: 'ingot', toType: 'ladle' }));
      }
      if (typeCount.roller && !typeCount.quench) {
        warnings.push(M('Rolled metal without a quench tank — hot metal needs cooling to set its structure.', { kind: 'add_and_connect', typeId: 'quench', toType: 'roller' }));
      }
    }

    if (activeKit === 'civil') {
      if (!typeCount.foundation) issues.push(M('No foundation — every structure starts with one.', { kind: 'add_and_connect', typeId: 'foundation', toType: 'column' }));
      else ok.push('Foundation ready.');
      if (typeCount.beam && !typeCount.column) {
        warnings.push(M('Beams need columns underneath.', { kind: 'add_and_connect', typeId: 'column', toType: 'beam' }));
      }
      if (typeCount.slab && !typeCount.beam) {
        warnings.push(M('Slabs sit on beams — add one.', { kind: 'insert_between', typeId: 'beam', betweenTypes: ['slab', 'column'] }));
      }
    }

    if (activeKit === 'carpentry') {
      if (typeCount.top && !typeCount.leg) {
        warnings.push(M('A table top needs legs.', { kind: 'add_and_connect', typeId: 'leg', toType: 'top' }));
      }
      if (typeCount.chair_back && !typeCount.top) {
        warnings.push(M('A chair back needs a seat.', { kind: 'add_and_connect', typeId: 'top', toType: 'chair_back' }));
      }
      if ((typeCount.plank || typeCount.top) && !typeCount.nail && !typeCount.screw) {
        warnings.push(M('Nothing holds the pieces together — add a nail or screw.', { kind: 'add_and_connect', typeId: 'nail', toType: 'plank' }));
      }
    }

    if (activeKit === 'welding') {
      if (!typeCount.plate) issues.push(M('No metal to weld — add a Plate.', { kind: 'add_only', typeId: 'plate' }));
      if (!typeCount.torch) issues.push(M('No torch — add one to start welding.', { kind: 'add_and_connect', typeId: 'torch', toType: 'plate' }));
      if (typeCount.torch && !typeCount.gas_cyl) {
        warnings.push(M('Torch without a gas cylinder — you have nothing to burn.', { kind: 'add_and_connect', typeId: 'gas_cyl', toType: 'torch' }));
      }
      if (typeCount.plate && !typeCount.ground_cable) {
        warnings.push(M('No ground cable — welding needs a return path.', { kind: 'add_and_connect', typeId: 'ground_cable', toType: 'plate' }));
      }
    }

    if (activeKit === 'fitters') {
      if (typeCount.bolt && !typeCount.nut) {
        issues.push(M('Bolt with no nut — nothing to fasten against.', { kind: 'add_and_connect', typeId: 'nut', toType: 'bolt' }));
      }
      if (typeCount.flange && !typeCount.gasket && (typeCount.flange >= 2)) {
        warnings.push(M('Two flanges without a gasket between — the joint will leak.', { kind: 'insert_between', typeId: 'gasket', betweenTypes: ['flange', 'flange'] }));
      }
      if ((typeCount.bolt || typeCount.nut) && !typeCount.washer) {
        warnings.push(M('No washer — fasteners can dig into the metal.', { kind: 'insert_between', typeId: 'washer', betweenTypes: ['bolt', 'nut'] }));
      }
    }

    // Generic connectivity — flag components with a single dangling wire on a critical hub
    Object.entries(degree).forEach(([pid, d]) => {
      const p = partById[pid]; if (!p) return;
      const ports = p.ports || [];
      if (ports.length >= 2 && d === 1 && !['tap', 'drain', 'shower', 'led', 'buzzer', 'ground', 'led2', 'ingot'].some((t) => p.id.startsWith(t + '_'))) {
        warnings.push(M(`${p.label} has only one wire — it looks like a dead-end.`));
      }
    });

    // Deduplicate by message text
    const dedupe = (arr) => {
      const seen = new Set(); const out = [];
      arr.forEach((x) => { const k = x.msg || x; if (!seen.has(k)) { seen.add(k); out.push(x); } });
      return out;
    };
    return { ok: [...new Set(ok)], issues: dedupe(issues), warnings: dedupe(warnings) };
  };

  // Fix-It — Phase 40.12. Apply a repair suggested by analyzeDesign.
  const applyFix = (fix) => {
    if (!fix || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const logicalW = rect.width / zoom;
    const logicalH = rect.height / zoom;
    const partDef = kit.parts.find((p) => p.id === fix.typeId);
    if (!partDef) { toast.error(`Fix unavailable — ${fix.typeId} not in this kit.`); return; }

    const findFirstOfType = (t) => placed.find((p) => p.id.startsWith(t + '_'));

    // Compute the drop position: near a related part if available, else centre.
    let x = logicalW / 2 - partDef.w / 2;
    let y = logicalH / 2 - partDef.h / 2;
    const anchor = fix.toType ? findFirstOfType(fix.toType) : null;
    if (anchor) {
      x = Math.min(logicalW - partDef.w, anchor.x + anchor.w + 24);
      y = Math.max(0, Math.min(logicalH - partDef.h, anchor.y + (anchor.h - partDef.h) / 2));
    }

    // Add the new part
    const newId = `${partDef.id}_${nextId.current++}`;
    const newPart = { ...partDef, id: newId, x, y };

    // Prepare connections based on fix kind
    let newConns = [];
    if (fix.kind === 'add_and_connect' && anchor) {
      const anchorRightPort = (anchor.ports || []).find((p) => p.id === 'R') ? 'R' : (anchor.ports || [])[0]?.id;
      const newLeftPort = (partDef.ports || []).find((p) => p.id === 'L') ? 'L' : (partDef.ports || [])[0]?.id;
      if (anchorRightPort && newLeftPort) {
        newConns.push({
          id: `w_${nextConnId.current++}`,
          fromPartId: anchor.id, fromPort: anchorRightPort,
          toPartId: newId, toPort: newLeftPort,
        });
      }
    } else if (fix.kind === 'insert_between' && fix.betweenTypes) {
      const [aType, bType] = fix.betweenTypes;
      // Find a wire that connects a → b (any direction) and splice this part in.
      // Fallback: match any wire whose FROM endpoint is aType so multi-hop
      // pipelines (e.g. tank→pipe→tap) still get repaired at their source.
      let wireIdx = connections.findIndex((c) => {
        const fromT = c.fromPartId.replace(/_\d+$/, '');
        const toT = c.toPartId.replace(/_\d+$/, '');
        return (fromT === aType && toT === bType) || (fromT === bType && toT === aType);
      });
      if (wireIdx < 0) {
        wireIdx = connections.findIndex((c) => {
          const fromT = c.fromPartId.replace(/_\d+$/, '');
          const toT = c.toPartId.replace(/_\d+$/, '');
          return fromT === aType || toT === aType;
        });
      }
      if (wireIdx >= 0) {
        const oldWire = connections[wireIdx];
        // Position new part between the two endpoints
        const a = _findPart(oldWire.fromPartId); const b = _findPart(oldWire.toPartId);
        if (a && b) {
          x = Math.max(0, Math.min(logicalW - partDef.w, (a.x + b.x + a.w) / 2 - partDef.w / 2));
          y = Math.max(0, Math.min(logicalH - partDef.h, (a.y + b.y + a.h) / 2 - partDef.h / 2));
          newPart.x = x; newPart.y = y;
        }
        const newL = (partDef.ports || []).find((p) => p.id === 'L') ? 'L' : (partDef.ports || [])[0]?.id;
        const newR = (partDef.ports || []).find((p) => p.id === 'R') ? 'R' : (partDef.ports || []).slice(-1)[0]?.id;
        newConns.push({ id: `w_${nextConnId.current++}`, fromPartId: oldWire.fromPartId, fromPort: oldWire.fromPort, toPartId: newId, toPort: newL });
        newConns.push({ id: `w_${nextConnId.current++}`, fromPartId: newId, fromPort: newR, toPartId: oldWire.toPartId, toPort: oldWire.toPort });
        // Remove old wire
        setConnections((prev) => [...prev.slice(0, wireIdx), ...prev.slice(wireIdx + 1), ...newConns]);
        setPlaced((prev) => [...prev, newPart]);
        toast.success(`Inserted ${partDef.label} into the line.`);
        // Re-run analysis
        return;
      }
      // Fallback: no matching wire → just add unconnected
    }

    setPlaced((prev) => [...prev, newPart]);
    if (newConns.length > 0) setConnections((prev) => [...prev, ...newConns]);
    toast.success(`Added ${partDef.label}${newConns.length ? ' and wired it in.' : '.'}`);
  };

  // Handy wrapper referencing latest placed
  const _findPart = (id) => placed.find((p) => p.id === id);

  const toggleRun = () => {
    if (connections.length === 0) {
      toast.error('Connect some components first, then hit Run');
      return;
    }
    const next = !running;
    setRunning(next);
    if (!next) setSimReport(null);
  };

  // Re-run design analysis whenever the design mutates while running, so Fix-It
  // buttons visibly resolve their warnings after the auto-repair kicks in.
  useEffect(() => {
    if (!running) return;
    setSimReport(analyzeDesign());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, placed, connections]);

  // Precompute wire paths (memoised on placed + connections)
  const wirePaths = useMemo(() => {
    const partsById = Object.fromEntries(placed.map((p) => [p.id, p]));
    return connections.map((c) => {
      const a = partsById[c.fromPartId]; const b = partsById[c.toPartId];
      if (!a || !b) return null;
      const pa = portPos(a, c.fromPort); const pb = portPos(b, c.toPort);
      return { id: c.id, d: curvedPath(pa, pb) };
    }).filter(Boolean);
  }, [placed, connections]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="engineering-designer-lab">
      {/* Inline styles for the flow animation */}
      <style>{`
        @keyframes engFlow { to { stroke-dashoffset: -28; } }
        .eng-wire-flow { stroke-dasharray: 10 8; animation: engFlow 0.9s linear infinite; }
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-[1920px] mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/labs')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <Sparkles className={`w-6 h-6 ${accent.text}`} />
            <div>
              <h1 className="text-sm font-bold">Engineering Designer</h1>
              <span className="text-[10px] text-muted-foreground">Wire-and-simulate workshop for {kit.label}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {Object.entries(KITS).map(([id, k]) => {
              const Icon = k.icon;
              return (
                <Button
                  key={id} size="sm"
                  variant={id === activeKit ? 'default' : 'outline'}
                  onClick={() => setActiveKit(id)}
                  className="h-8 text-[11px]"
                  data-testid={`kit-btn-${id}`}
                >
                  <Icon className="w-3.5 h-3.5 mr-1" />{k.label}
                </Button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* PALETTE */}
        <aside className="w-64 border-r border-border bg-muted/30 overflow-y-auto shrink-0" data-testid="palette">
          <div className="p-3">
            <div className={`text-[10px] uppercase tracking-wide ${accent.text} font-bold mb-2`}>Palette</div>
            <div className="flex flex-col gap-1.5" data-testid="palette-list">
              {kit.parts.map((part) => {
                const spec = PART_SPECS[part.id.replace(/_\d+$/, '')];
                return (
                  <button
                    key={part.id}
                    onClick={() => handlePaletteClick(part)}
                    className={`p-2 rounded-lg border ${accent.border} ${accent.bg} hover:scale-[1.02] hover:shadow-md transition-all text-left flex items-center gap-2 group`}
                    data-testid={`palette-part-${part.id}`}
                    title={spec ? `${spec.title} — ${spec.spec}` : part.label}
                  >
                    <div className="text-xl leading-none w-6 text-center shrink-0">{part.emoji}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10.5px] font-semibold text-foreground leading-tight truncate">{spec?.title || part.label}</div>
                      {spec && (
                        <div className="text-[8.5px] font-mono text-emerald-600 dark:text-emerald-400 leading-tight truncate">{spec.spec}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className={`mt-4 p-2.5 rounded-lg border ${accent.border} bg-background/60`}>
              <div className={`text-[10px] uppercase tracking-wide ${accent.text} font-bold mb-1`}>How it works</div>
              <ol className="text-[10.5px] leading-relaxed text-muted-foreground list-decimal list-inside space-y-0.5">
                <li>Click a part → click canvas to place.</li>
                <li>Click a small white dot (port) → click another port to wire them.</li>
                <li>Hit <b className={accent.text}>Run</b> to see {kit.flowLabel.toLowerCase()} flow.</li>
              </ol>
            </div>

            <div className={`mt-3 p-2.5 rounded-lg border ${accent.border} bg-background/60`}>
              <div className={`text-[10px] uppercase tracking-wide ${accent.text} font-bold mb-1`}>Try Building</div>
              <ol className="text-[11px] leading-relaxed text-muted-foreground list-decimal list-inside space-y-1">
                {kit.challenges.map((c, i) => <li key={i}>{c}</li>)}
              </ol>
            </div>

            {/* Starter blueprints */}
            {(TEMPLATES[activeKit] || []).length > 0 && (
              <div className={`mt-3 p-2.5 rounded-lg border ${accent.border} bg-background/60`} data-testid="templates-panel">
                <div className={`text-[10px] uppercase tracking-wide ${accent.text} font-bold mb-2 flex items-center justify-between gap-1`}>
                  <span className="flex items-center gap-1"><BookMarked className="w-3 h-3" /> Blueprints</span>
                  <span className="text-[9px] text-muted-foreground">{TEMPLATES[activeKit].length} total</span>
                </div>

                {/* Difficulty filter chips */}
                <div className="flex flex-wrap gap-1 mb-2" data-testid="difficulty-filter">
                  <button
                    onClick={() => setActiveTier('all')}
                    className={`text-[9.5px] px-2 py-0.5 rounded-full border ${activeTier === 'all' ? `${accent.btn} text-white border-transparent` : `${accent.border} text-muted-foreground hover:${accent.bg}`}`}
                    data-testid="tier-all"
                  >All</button>
                  {DIFFICULTY_TIERS.map((t) => {
                    const count = TEMPLATES[activeKit].filter((x) => x.difficulty === t.id).length;
                    if (count === 0) return null;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActiveTier(t.id)}
                        className={`text-[9.5px] px-2 py-0.5 rounded-full border ${activeTier === t.id ? `${accent.btn} text-white border-transparent` : `${accent.border} ${t.colour} hover:${accent.bg}`}`}
                        data-testid={`tier-${t.id}`}
                      >{t.label} · {count}</button>
                    );
                  })}
                </div>

                {/* Grouped list (or filtered) */}
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {DIFFICULTY_TIERS.map((tier) => {
                    if (activeTier !== 'all' && activeTier !== tier.id) return null;
                    const items = TEMPLATES[activeKit].filter((x) => x.difficulty === tier.id);
                    if (items.length === 0) return null;
                    return (
                      <div key={tier.id} data-testid={`tier-group-${tier.id}`}>
                        {activeTier === 'all' && (
                          <div className={`text-[9.5px] uppercase font-bold ${tier.colour} mb-1`}>{tier.label}</div>
                        )}
                        <div className="space-y-1.5">
                          {items.map((tpl, i) => {
                            const globalIdx = TEMPLATES[activeKit].indexOf(tpl);
                            return (
                              <button
                                key={`${tier.id}-${i}`}
                                onClick={() => loadTemplate(tpl)}
                                className={`w-full text-left p-2 rounded-md border ${accent.border} hover:${accent.bg} transition-colors`}
                                data-testid={`template-${activeKit}-${globalIdx}`}
                              >
                                <div className={`text-[11px] font-semibold ${accent.text}`}>{tpl.name}</div>
                                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{tpl.description}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[9px] text-muted-foreground mt-2">Loading a template replaces your current design.</div>
              </div>
            )}

            {personalTemplates.length > 0 && (
              <div className={`mt-3 p-2.5 rounded-lg border ${accent.border} bg-background/60`} data-testid="my-templates-panel">
                <div className={`text-[10px] uppercase tracking-wide ${accent.text} font-bold mb-2 flex items-center gap-1`}>
                  <BookMarked className="w-3 h-3" /> My Templates
                </div>
                <div className="space-y-1.5">
                  {personalTemplates.map((tpl, i) => (
                    <div key={i} className={`flex items-center gap-1 border ${accent.border} rounded-md`} data-testid={`my-template-${i}`}>
                      <button onClick={() => loadTemplate(tpl)} className={`flex-1 text-left p-2 hover:${accent.bg} transition-colors rounded-md`}>
                        <div className={`text-[11px] font-semibold ${accent.text} truncate`}>{tpl.name}</div>
                        <div className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{tpl.description}</div>
                      </button>
                      <button onClick={() => deletePersonalTemplate(i)} className="w-6 h-6 mr-1 rounded-md text-[11px] text-muted-foreground hover:bg-red-500/20 hover:text-red-500" title="Delete" data-testid={`delete-my-template-${i}`}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* CANVAS */}
        <main className="flex-1 relative overflow-hidden bg-slate-950" data-testid="designer-canvas-wrap">
          {/* Toolbar */}
          <div className="absolute top-2 right-2 z-30 flex gap-1 flex-wrap items-center">
            {/* Zoom slider — Phase 40.12 */}
            <div className="flex items-center gap-1.5 h-8 px-2 rounded-md border border-border bg-background/85 backdrop-blur" data-testid="zoom-control">
              <button
                onClick={() => setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10))}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-muted text-sm font-bold"
                title="Zoom out"
                data-testid="zoom-out-btn"
              >−</button>
              <input
                type="range"
                min="0.4" max="2" step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-24 accent-indigo-500 cursor-pointer"
                data-testid="zoom-slider"
                title={`Zoom ${Math.round(zoom * 100)}%`}
              />
              <button
                onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10))}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-muted text-sm font-bold"
                title="Zoom in"
                data-testid="zoom-in-btn"
              >+</button>
              <button
                onClick={() => setZoom(1)}
                className="text-[10px] px-1.5 h-6 rounded hover:bg-muted text-muted-foreground tabular-nums min-w-[38px]"
                title="Reset zoom"
                data-testid="zoom-reset-btn"
              >{Math.round(zoom * 100)}%</button>
            </div>

            <Button size="sm" variant={running ? 'default' : 'outline'} onClick={toggleRun} className={`h-8 text-[11px] ${running ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : ''}`} data-testid="run-btn">
              {running ? <><Pause className="w-3.5 h-3.5 mr-1" />Stop</> : <><Play className="w-3.5 h-3.5 mr-1" />Run</>}
            </Button>
            <Button size="sm" variant="outline" onClick={clearAll} className="h-8 text-[11px]" data-testid="clear-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear</Button>
            <Button size="sm" variant="outline" onClick={exportDesign} className="h-8 text-[11px]" data-testid="export-btn"><Download className="w-3.5 h-3.5 mr-1" />Export</Button>
            <Button size="sm" variant="outline" onClick={exportBlueprint} className="h-8 text-[11px]" data-testid="export-blueprint-btn"><Download className="w-3.5 h-3.5 mr-1" />Blueprint</Button>
            <Button size="sm" variant="outline" onClick={saveAsPersonalTemplate} className="h-8 text-[11px]" data-testid="save-tpl-btn"><BookMarked className="w-3.5 h-3.5 mr-1" />Save Template</Button>
            <Button size="sm" className={`h-8 text-[11px] ${accent.btn} text-white`} onClick={saveDesign} data-testid="save-btn"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
          </div>

          <AnimatePresence>
            {savedFlash && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute top-12 right-2 z-40 bg-emerald-500 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1" data-testid="saved-toast">
                <CheckCircle className="w-3.5 h-3.5" /> Saved
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 40.17 — Prompt bar for natural-language scene building */}
          <div className="absolute top-12 left-2 right-2 z-30 flex items-center gap-2 bg-slate-900/80 backdrop-blur border border-white/15 rounded-xl px-2.5 py-1.5 shadow-lg" data-testid="prompt-bar">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
            <input
              type="text"
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { applyPrompt(promptInput); setPromptInput(''); } }}
              placeholder='Try "empty kitchen" then "add cabinet", "add sink", "add tap"…'
              className="flex-1 bg-transparent border-none outline-none text-white text-xs placeholder:text-white/40 min-w-0"
              data-testid="prompt-input"
            />
            <div className="hidden md:flex items-center gap-1">
              {['empty kitchen', 'empty bathroom', 'add cabinet', 'add sink', 'add tap', 'add toilet', 'clear'].map((s) => (
                <button
                  key={s}
                  onClick={() => applyPrompt(s)}
                  className="px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-[10px] font-semibold border border-white/10 transition-colors whitespace-nowrap"
                  data-testid={`prompt-chip-${s.replace(/\s+/g, '-')}`}
                >{s}</button>
              ))}
            </div>
            <Button
              size="sm"
              className="h-7 text-[10px] bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={() => { applyPrompt(promptInput); setPromptInput(''); }}
              data-testid="prompt-submit"
            >Build</Button>
          </div>

          {/* Grid background */}
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            className="w-full h-full relative overflow-auto"
            data-testid="designer-canvas"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
            }}
          >
            {/* Real-photo backdrop layer.  Loads when promptScene is set OR
                when the kit has a photo default.  Vignette overlay keeps the
                canvas readable while the underlying photo makes the student
                actually feel inside the trade.  Also swaps to a *context*
                photo (e.g. kitchen-with-cabinet) matching the most-recently
                added furniture so the room grows around the student. */}
            {(() => {
              const scenesKey = promptScene || (PHOTO_BG[activeKit] ? activeKit : null);
              // Pick a context photo if the base scene has one and the
              // student has added recognised furniture.
              let photo = PHOTO_BG[scenesKey];
              if (scenesKey && CONTEXT_PHOTO[scenesKey]) {
                const bank = CONTEXT_PHOTO[scenesKey];
                for (let i = placed.length - 1; i >= 0; i--) {
                  const base = placed[i].id.replace(/_\d+$/, '');
                  const key =
                    base === 'cabinet' ? 'cabinet' :
                    base === 'sink_kitchen' ? 'sink' :
                    base === 'tap' ? 'tap' :
                    base === 'stove' ? 'stove' :
                    base === 'toilet' ? 'toilet' :
                    base === 'bathtub' ? 'bathtub' :
                    base === 'shower_stall' ? 'shower' : null;
                  if (key && bank[key]) { photo = bank[key]; break; }
                }
                if (!photo && bank.base) photo = bank.base;
              }
              const SceneComp = scenesKey ? SCENES[scenesKey] : null;
              if (!photo && !SceneComp) return null;
              return (
                <>
                  {photo && (
                    <div
                      className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                      data-testid={`photo-bg-${scenesKey}`}
                      style={{
                        backgroundImage: `linear-gradient(rgba(2,6,23,0.35), rgba(2,6,23,0.72)), url('${photo}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'saturate(0.9) contrast(0.98)',
                      }}
                    />
                  )}
                  {SceneComp && (
                    <div className="absolute inset-0 pointer-events-none" data-testid={`scene-${scenesKey}`} style={{ opacity: 0.4, mixBlendMode: 'multiply' }}>
                      {SceneComp()}
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
                    <div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-[11px] font-semibold text-white/90 border border-white/15 shadow-lg">
                      <span className="text-amber-300 mr-1.5">◉</span>
                      {BG_LABEL[scenesKey] || scenesKey}
                    </div>
                  </div>
                </>
              );
            })()}

            {placed.length === 0 && showTip && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center max-w-xs">
                  <div className="text-5xl mb-2">🖱️</div>
                  <div className="text-sm text-muted-foreground">
                    Click a palette part, then click here to drop it. Wire ports (small white dots) to build a flow, then press <b className={accent.text}>Run</b>.
                  </div>
                </div>
              </div>
            )}

            {/* Scaled contents — parts + wire SVG live inside this wrapper so
                CSS transform: scale() zooms them uniformly.  Logical (unscaled)
                coordinates are preserved everywhere else in the code. */}
            <div
              className="absolute top-0 left-0"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
                width: `${100 / zoom}%`,
                height: `${100 / zoom}%`,
              }}
              data-testid="designer-canvas-scaled"
            >
              {/* SVG overlay for wires */}
              <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%" data-testid="wire-svg" style={{ zIndex: 3 }}>
                {wirePaths.map((w) => (
                  <g key={w.id}>
                    <path d={w.d} stroke={kit.flowColour} strokeWidth={3} fill="none" opacity={running ? 0.35 : 0.75} />
                    {running && (
                      <path d={w.d} stroke={kit.flowColour} strokeWidth={4} fill="none" className="eng-wire-flow" opacity={0.95} />
                    )}
                    {!running && (
                      <MidpointDelete d={w.d} onClick={() => deleteConnection(w.id)} accent={kit.flowColour} testId={`wire-del-${w.id}`} />
                    )}
                  </g>
                ))}
              </svg>

              {/* Parts */}
              {placed.map((p) => (
                <CanvasPart key={p.id} p={p} onMove={movePart} onDelete={deletePart} onPortClick={onPortClick} canvasRef={canvasRef} activePort={activePort} running={running} accent={accent} zoom={zoom} onHover={setHoveredPart} />
              ))}

              {/* Spec callout — Phase 40.15.  Shows a real-world blueprint
                  spec (e.g. "3/4-inch copper · 60 psi") next to the hovered
                  part so the lab feels like a real trade drawing. */}
              {hoveredPart && (() => {
                const spec = PART_SPECS[hoveredPart.id.replace(/_\d+$/, '')];
                if (!spec) return null;
                const cx = hoveredPart.x + hoveredPart.w + 12;
                const cy = hoveredPart.y - 4;
                return (
                  <div
                    className="absolute pointer-events-none z-30 max-w-[230px] px-2.5 py-1.5 rounded-md bg-slate-900/95 border border-white/20 shadow-xl backdrop-blur"
                    style={{ left: cx, top: Math.max(0, cy) }}
                    data-testid={`spec-callout-${hoveredPart.id}`}
                  >
                    <div className="text-[11px] font-black text-white leading-tight">{spec.title}</div>
                    <div className="text-[10px] text-emerald-200 font-mono mt-0.5 leading-tight">{spec.spec}</div>
                    <svg className="absolute -left-2 top-2.5 w-2 h-2" viewBox="0 0 8 8"><path d="M8 0 L0 4 L8 8 Z" fill="rgba(15,23,42,0.95)" /></svg>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Wire Colour Legend — always visible bottom-right */}
          <div className="absolute bottom-2 right-2 z-20 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/85 border border-white/10 backdrop-blur" data-testid="wire-legend">
            <Info className="w-3 h-3 text-white/60" />
            <span className="text-[10px] text-white/70 font-semibold">Flow:</span>
            <span className="flex items-center gap-1 text-[10px] text-white/85" title={`${kit.label} — ${kit.flowLabel}`}>
              <span className="inline-block w-4 h-1 rounded-full" style={{ background: kit.flowColour }} />
              {kit.flowLabel}
            </span>
            <span className="text-white/30 mx-1">·</span>
            <span className="flex items-center gap-1 text-[10px] text-white/60">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-white border border-slate-900" />
              Port
            </span>
          </div>

          {/* Simulation Report Pop-out — appears after Run */}
          <AnimatePresence>
            {running && simReport && (
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.25 }}
                className="absolute top-14 right-2 z-30 w-80 max-w-[calc(100vw-2rem)] p-3 rounded-2xl bg-slate-900/95 border border-white/15 shadow-2xl backdrop-blur"
                data-testid="sim-report"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className={`w-4 h-4 ${accent.text}`} />
                    <h4 className="text-sm font-bold text-white">Simulation Report</h4>
                  </div>
                  <button
                    onClick={() => setSimReport(null)}
                    className="text-white/50 hover:text-white text-xs"
                    data-testid="sim-report-close"
                    title="Close"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-[10.5px] text-white/70 mb-2 leading-relaxed">
                  Verdict for your {kit.label.toLowerCase()} design (heuristic — teacher rules, not a physics engine).
                </p>

                {simReport.issues.length === 0 && simReport.warnings.length === 0 && (
                  <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-[11px] text-emerald-200" data-testid="sim-ok-banner">
                    <CheckCircle className="w-3.5 h-3.5 inline-block mr-1" />
                    Looks great — no obvious issues. Ship it!
                  </div>
                )}

                {simReport.issues.length > 0 && (
                  <div className="mb-2" data-testid="sim-issues">
                    <div className="text-[10px] uppercase font-bold text-rose-300 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Critical issues</div>
                    <ul className="space-y-1.5">
                      {simReport.issues.map((s, i) => (
                        <li key={i} className="text-[11px] text-rose-100 leading-tight pl-2 border-l-2 border-rose-500">
                          <div>{s.msg || s}</div>
                          {s.fix && (
                            <button
                              onClick={() => applyFix(s.fix)}
                              className="mt-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold"
                              data-testid={`fix-issue-${i}`}
                              title="Auto-add the missing part"
                            >Fix for me</button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {simReport.warnings.length > 0 && (
                  <div className="mb-2" data-testid="sim-warnings">
                    <div className="text-[10px] uppercase font-bold text-amber-300 mb-1">Warnings</div>
                    <ul className="space-y-1.5">
                      {simReport.warnings.map((s, i) => (
                        <li key={i} className="text-[11px] text-amber-100 leading-tight pl-2 border-l-2 border-amber-500">
                          <div>{s.msg || s}</div>
                          {s.fix && (
                            <button
                              onClick={() => applyFix(s.fix)}
                              className="mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 hover:bg-amber-400 text-white font-semibold"
                              data-testid={`fix-warning-${i}`}
                              title="Auto-add the missing part"
                            >Fix for me</button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {simReport.ok.length > 0 && (
                  <div data-testid="sim-good">
                    <div className="text-[10px] uppercase font-bold text-emerald-300 mb-1">What is working</div>
                    <ul className="space-y-1">
                      {simReport.ok.map((s, i) => (
                        <li key={i} className="text-[11px] text-emerald-100 leading-tight pl-2 border-l-2 border-emerald-500">{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats footer */}
          <div className="absolute bottom-2 left-2 z-20 flex gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px]" data-testid="stat-parts">{placed.length} parts</Badge>
            <Badge variant="outline" className="text-[10px]" data-testid="stat-wires"><Link2 className="w-3 h-3 mr-0.5" />{connections.length} wires</Badge>
            <Badge variant="outline" className={`text-[10px] ${accent.text}`}>{kit.label}</Badge>
            {running && (
              <Badge className="text-[10px] bg-emerald-500 text-white animate-pulse" data-testid="stat-flowing">● {kit.flowLabel} flowing</Badge>
            )}
            {activePort && (
              <Badge className="text-[10px] bg-amber-500 text-white" data-testid="stat-linking">Pick another port to connect (or click canvas to cancel)</Badge>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MidpointDelete — small clickable × on the wire midpoint (uses foreignObject
// so we get real click events inside the SVG overlay).
// -----------------------------------------------------------------------------
function MidpointDelete({ d, onClick, accent, testId }) {
  // parse path "M ax ay C ... ex ey"
  const nums = d.match(/-?\d+(\.\d+)?/g)?.map(Number) || [];
  if (nums.length < 4) return null;
  const ax = nums[0], ay = nums[1];
  const ex = nums[nums.length - 2], ey = nums[nums.length - 1];
  const mx = (ax + ex) / 2, my = (ay + ey) / 2;
  return (
    <foreignObject x={mx - 8} y={my - 8} width={16} height={16} style={{ pointerEvents: 'auto' }}>
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        title="Remove wire"
        data-testid={testId}
        style={{
          width: 16, height: 16, borderRadius: '50%',
          background: '#0f172a', color: accent, border: `1px solid ${accent}`,
          fontSize: 10, lineHeight: '14px', textAlign: 'center', cursor: 'pointer',
          padding: 0,
        }}
      >×</button>
    </foreignObject>
  );
}
